import React, { useState, useRef, useCallback, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../../hooks/useToast";

/**
 * Post Form Component
 * Allows users to post messages and replies in threads
 * Requirements: 3.1, 7.2, 7.3, 7.4
 *
 * @param {Object} props - Component props
 * @param {string} props.threadId - The thread ID to post message to
 * @param {string} props.parentMessageId - Optional parent message ID for replies
 * @param {Function} props.onSubmit - Callback when message is posted successfully
 * @param {Function} props.onError - Optional error handler callback
 * @param {string} props.placeholder - Input placeholder text
 * @param {number} props.maxLength - Maximum character length (default: 500)
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 * @param {string} props.className - Additional CSS classes
 */
const PostForm = ({
  threadId,
  parentMessageId,
  onSubmit,
  onError,
  placeholder = "Write a message...",
  maxLength = 500,
  disabled = false,
  autoFocus = false,
  className = "",
}) => {
  const { user } = useAuth0();
  const toast = useToast();
  const textareaRef = useRef(null);

  // Form state
  const [formState, setFormState] = useState({
    content: "",
    isSubmitting: false,
    error: null,
    characterCount: 0,
    rows: 1,
  });

  /**
   * Update character count and adjust textarea height
   */
  const updateTextarea = useCallback((value) => {
    const characterCount = value.length;

    // Calculate rows based on content
    const lines = value.split("\n").length;
    const contentRows = Math.max(1, Math.min(lines, 6)); // Max 6 rows

    setFormState((prev) => ({
      ...prev,
      content: value,
      characterCount,
      rows: contentRows,
    }));
  }, []);

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Prevent input if over character limit
      if (value.length > maxLength) {
        return;
      }

      updateTextarea(value);

      // Clear error when user starts typing
      if (formState.error) {
        setFormState((prev) => ({ ...prev, error: null }));
      }
    },
    [maxLength, formState.error, updateTextarea]
  );

  /**
   * Validate form content
   */
  const validateContent = useCallback(
    (content) => {
      const trimmed = content.trim();

      if (!trimmed) {
        return "Message content cannot be empty";
      }

      if (trimmed.length > maxLength) {
        return `Message cannot exceed ${maxLength} characters`;
      }

      return null;
    },
    [maxLength]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async () => {
    if (!threadId) {
      const error = "Thread ID is required";
      setFormState((prev) => ({ ...prev, error }));
      onError?.(new Error(error));
      return;
    }

    // Validate content
    const validationError = validateContent(formState.content);
    if (validationError) {
      setFormState((prev) => ({ ...prev, error: validationError }));
      return;
    }

    // Set submitting state
    setFormState((prev) => ({
      ...prev,
      isSubmitting: true,
      error: null,
    }));

    try {
      // Call the onSubmit callback with form data
      const messageData = {
        threadId,
        parentMessageId,
        content: formState.content.trim(),
      };

      await onSubmit?.(messageData);

      // Reset form on success
      setFormState((prev) => ({
        ...prev,
        content: "",
        characterCount: 0,
        rows: 1,
        isSubmitting: false,
        error: null,
      }));

      // Show success toast
      toast.success("Message posted successfully!");

      // Focus back to textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (error) {
      let errorMessage = "Failed to post message";

      if (error.type === "AUTH_ERROR") {
        errorMessage = "Authentication required - please log in";
      } else if (error.type === "RATE_LIMIT_ERROR") {
        errorMessage =
          "You are posting too quickly - please wait before trying again";
      } else if (error.type === "NETWORK_ERROR") {
        errorMessage =
          "Connection error - please check your internet connection";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: errorMessage,
      }));

      onError?.(error);
    }
  }, [
    threadId,
    parentMessageId,
    formState.content,
    validateContent,
    onSubmit,
    onError,
    toast,
  ]);

  /**
   * Handle key press events
   */
  const handleKeyPress = useCallback(
    (e) => {
      // Submit on Enter (but not Shift+Enter)
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  /**
   * Handle form reset
   */
  const handleReset = useCallback(() => {
    setFormState({
      content: "",
      isSubmitting: false,
      error: null,
      characterCount: 0,
      rows: 1,
    });

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  /**
   * Auto-focus on mount if requested
   */
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  /**
   * Get character count color based on usage
   */
  const getCharacterCountColor = useCallback(() => {
    const percentage = (formState.characterCount / maxLength) * 100;

    if (percentage >= 90) return "text-red-500";
    if (percentage >= 75) return "text-yellow-500";
    return "text-medium-grey";
  }, [formState.characterCount, maxLength]);

  /**
   * Check if form can be submitted
   */
  const canSubmit =
    !disabled &&
    !formState.isSubmitting &&
    formState.content.trim().length > 0 &&
    formState.characterCount <= maxLength;

  return (
    <div className={`post-form ${className}`}>
      {/* Form Container */}
      <div className="flex items-end space-x-3">
        {/* User Avatar */}
        <div className="flex-shrink-0">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || user.nickname || "User"}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 bg-medium-grey rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {(user?.name || user?.nickname || "U").charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Input Container */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              rows={formState.rows}
              value={formState.content}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={disabled || formState.isSubmitting}
              className={`
                block w-full resize-none border rounded-card px-3 py-2 text-sm 
                placeholder-medium-grey transition-colors
                ${
                  formState.error
                    ? "border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-gray-300 focus:border-deep-red focus:ring-1 focus:ring-deep-red"
                }
                ${
                  disabled || formState.isSubmitting
                    ? "bg-gray-100 cursor-not-allowed"
                    : "bg-white"
                }
              `}
              placeholder={disabled ? "Unable to send messages" : placeholder}
              style={{
                minHeight: "40px",
                maxHeight: "120px",
                transition: "height 0.1s ease-out",
              }}
            />

            {/* Character Count */}
            <div className="absolute right-2 bottom-2 flex items-center space-x-2">
              <span className={`text-xs ${getCharacterCountColor()}`}>
                {formState.characterCount}/{maxLength}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {formState.error && (
            <div className="mt-2 flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-red-500 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-red-600 break-words">
                {formState.error}
              </p>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex-shrink-0">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`
              inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium 
              rounded-card transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
              ${
                canSubmit
                  ? "text-white bg-deep-red hover:bg-bright-red focus:ring-deep-red"
                  : "text-gray-400 bg-gray-300 cursor-not-allowed"
              }
            `}
            title={canSubmit ? "Send message" : "Enter a message to send"}
          >
            {formState.isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Form Actions (for replies or special cases) */}
      {parentMessageId && (
        <div className="mt-2 flex items-center justify-between text-xs text-medium-grey">
          <span>Replying to message</span>
          <button
            onClick={handleReset}
            className="text-deep-red hover:text-bright-red transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      <div className="mt-1 text-xs text-medium-grey">
        <span>Press Enter to send, Shift+Enter for new line</span>
      </div>
    </div>
  );
};

export default PostForm;
